/* eslint-disable import/no-duplicates */
import { ReactElement, ReactNode } from 'react'
import { NextPage } from 'next'
import type { AppContext, AppProps } from 'next/app'
import { default as NextApp } from 'next/app'
import dynamic from 'next/dynamic'

import Loader from '@/components/base/Loader'
import SVGSprites from '@/components/base/SVGSprites'
import Header from '@/components/layout/Header'
import Landing from '@/components/layout/Landing'
import { MixerProvider } from '@/contexts/mixer'
import { Store, StoreProvider } from '@/contexts/store'
import { ThemeProvider } from '@/contexts/theme'
import { TimelineProvider } from '@/contexts/timeline'
import { TransitionProvider } from '@/contexts/transition'
import { mapOptionsDataProps } from '@/utils/page-data'
import { getPromiseFromFetch } from '@/utils/request'
import { API, MOCK_DATA } from '@/constants'

import '@/styles/globals.css'

import '@/base/gsap'

const GridHelper = dynamic(() => import('@/components/base/GridHelper'), {
  ssr: false
})

const Stage = dynamic(() => import('@/webgl/Stage'), {
  ssr: false
})

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  // eslint-disable-next-line no-unused-vars
  getLayout?: (page: ReactElement, props: AppProps) => ReactNode
}

export default function App({
  Component,
  pageProps,
  menu,
  strings
}: AppProps & Store & { Component: NextPageWithLayout }) {
  const getLayout = Component.getLayout ?? ((page) => page)

  return (
    <StoreProvider home={pageProps.homeProps || pageProps} menu={menu} strings={strings}>
      <ThemeProvider fluid>
        <MixerProvider>
          <TimelineProvider>
            {process.env.NODE_ENV !== 'production' ? <GridHelper /> : null}
            <Stage />
            <Loader />
            <Header />
            <Landing />
            <TransitionProvider>
              {getLayout(<Component {...pageProps} />, pageProps)}
            </TransitionProvider>
            <SVGSprites />
          </TimelineProvider>
        </MixerProvider>
      </ThemeProvider>
    </StoreProvider>
  )
}

App.getInitialProps = async (appContext: AppContext) => {
  const appProps = await NextApp.getInitialProps(appContext)
  const lang = appContext.router.locale

  const { data: menuData } = MOCK_DATA
    ? { data: require(`@/data/menu_locations.json`) }
    : await getPromiseFromFetch({
        method: 'get',
        url: `${API.MENU_LOCATIONS}?lang=${lang}`
      })

  const { data: optionsData } = MOCK_DATA
    ? { data: require(`@/data/options.json`) }
    : await getPromiseFromFetch({
        method: 'get',
        url: `${API.STRINGS}?lang=${lang}`
      })

  return {
    ...appProps,
    strings: await mapOptionsDataProps(optionsData),
    menu: Object.keys(menuData).length > 0 ? menuData : {}
  }
}
